import { useTextStyle } from '../../../hooks/useTextStyle';
import adjustHeight from '../../../utils/thumbnail';
import { commonWords } from '../../../utils/commonWords';
import Item from './Item';

import styles from './SearchResult.module.css';

// Simple hash function for keys (no bitwise)
const hashString = (str: string) => `${str.length}-${encodeURIComponent(str)}`;

const highlightText = (inputText: string, highlights: string[]): React.ReactNode => {
  // Step 1: Normalize and prepare input text
  // - Normalize text to handle different unicode representations
  // - Trim whitespace to ensure consistent matching
  const normalizedInputText = inputText.normalize('NFC').trim();

  // Step 2: Process highlights array
  // - Normalize each highlight to handle different unicode representations
  // - Remove empty strings
  // - Preserve original order and duplicates for exact phrase matching
  const phraseHighlights = highlights.map(h => h.normalize('NFC').trim()).filter(h => h !== '');

  // Step 3: Filter out common words for individual word highlighting
  // - This is only used for individual word highlighting, not for full phrases
  // - Common words are still included in full phrase matching
  const filteredHighlights = phraseHighlights.filter(h => !commonWords.has(h.toLowerCase()));

  // Early return if no highlights to process
  if (!phraseHighlights.length) {
    return normalizedInputText;
  }

  // Step 4: Detect language characteristics
  // - Check if text is in a non-space-separated language (e.g., Chinese, Japanese)
  // - This affects how we handle word boundaries and matching
  const isNonSpaceSeparatedLanguage = (text: string): boolean => {
    const cjkRegex = /[\u4E00-\u9FFF\u3040-\u30FF\uAC00-\uD7AF]/; // Chinese, Japanese, Korean
    const thaiRegex = /[\u0E00-\u0E7F]/; // Thai
    const arabicRegex = /[\u0600-\u06FF]/; // Arabic
    const hebrewRegex = /[\u0590-\u05FF]/; // Hebrew
    const devanagariRegex = /[\u0900-\u097F]/; // Hindi (Devanagari)
    const bengaliRegex = /[\u0980-\u09FF]/; // Bengali
    const ethiopicRegex = /[\u1200-\u137F]/; // Amharic (Ethiopic)
    return (
      cjkRegex.test(text) ||
      thaiRegex.test(text) ||
      arabicRegex.test(text) ||
      hebrewRegex.test(text) ||
      devanagariRegex.test(text) ||
      bengaliRegex.test(text) ||
      ethiopicRegex.test(text)
    );
  };

  // Step 5: Detect text direction
  // - Check if text is right-to-left (RTL) for proper rendering
  // - Used for languages like Arabic and Hebrew
  const isRTL = (text: string): boolean => {
    const arabicRegex = /[\u0600-\u06FF]/; // Arabic
    const hebrewRegex = /[\u0590-\u05FF]/; // Hebrew
    return arabicRegex.test(text) || hebrewRegex.test(text);
  };

  // Step 6: Helper function to escape regex special characters
  // - Ensures search terms with special characters are matched literally
  const escapeRegex = (str: string): string => str.replace(/[.*+?^${}()|[\]\\'\u2019]/g, '\\$&');

  // Step 7: Try matching the entire phrase first
  // - Construct full phrase from highlights array
  // - Preserve original word order and duplicates
  const fullPhrase = phraseHighlights
    .map(h => h.replace(/[^\p{L}\p{N}\s'\u2019]/gu, '')) // Keep letters, numbers, spaces, apostrophes
    .join(' ') // Join with space to preserve original phrase structure
    .trim();

  if (fullPhrase) {
    // Step 7.1: Create flexible regex for phrase matching
    // - Split into words to handle variable spacing
    // - Allow for flexible whitespace between words
    const phraseWords = fullPhrase.split(/\s+/);
    const phraseRegexStr = phraseWords.map(word => escapeRegex(word)).join('\\s+');
    const phraseRegex = new RegExp(phraseRegexStr, 'gi');

    // Step 7.2: Apply phrase matching
    // - Find all matches of the complete phrase
    // - Split text into parts and highlight matches
    const phraseMatches = normalizedInputText.match(phraseRegex);
    if (phraseMatches) {
      const parts = normalizedInputText.split(phraseRegex);
      return parts.map((part, index) => {
        const match = phraseMatches[index];
        const key = `phrase-${hashString(part)}-${index}`;
        return (
          <span key={key} dir={isRTL(part) ? 'rtl' : 'ltr'}>
            {part}
            {match && (
              <span className={styles.highlight} dir={isRTL(match) ? 'rtl' : 'ltr'}>
                {match}
              </span>
            )}
          </span>
        );
      });
    }
  }

  // Step 8: Handle space-separated languages
  // - Only highlight individual non-common words
  // - Use word boundaries for precise matching
  if (!isNonSpaceSeparatedLanguage(normalizedInputText)) {
    if (!filteredHighlights.length) {
      return <span dir={isRTL(normalizedInputText) ? 'rtl' : 'ltr'}>{normalizedInputText}</span>;
    }
    const wordRegexStr = filteredHighlights
      .map(h => {
        const cleanWord = h.replace(/[^\p{L}\p{N}\s'\u2019]/gu, '').trim();
        return cleanWord ? `\\b${escapeRegex(cleanWord)}\\b` : '';
      })
      .filter(Boolean)
      .join('|');
    if (!wordRegexStr) {
      return <span dir={isRTL(normalizedInputText) ? 'rtl' : 'ltr'}>{normalizedInputText}</span>;
    }
    const wordRegex = new RegExp(wordRegexStr, 'gi');
    const wordMatches = normalizedInputText.match(wordRegex);
    if (!wordMatches) {
      return <span dir={isRTL(normalizedInputText) ? 'rtl' : 'ltr'}>{normalizedInputText}</span>;
    }
    const parts = normalizedInputText.split(wordRegex);
    return parts.map((part, index) => {
      const match = wordMatches[index];
      const key = `word-${hashString(part)}-${index}`;
      return (
        <span key={key} dir={isRTL(part) ? 'rtl' : 'ltr'}>
          {part}
          {match && (
            <span className={styles.highlight} dir={isRTL(match) ? 'rtl' : 'ltr'}>
              {match}
            </span>
          )}
        </span>
      );
    });
  }

  // Step 9: Handle non-space-separated languages
  // - Match exact sequences without word boundaries
  // - Sort highlights by length to prioritize longer matches
  if (isNonSpaceSeparatedLanguage(normalizedInputText)) {
    if (!filteredHighlights.length) {
      return <span dir={isRTL(normalizedInputText) ? 'rtl' : 'ltr'}>{normalizedInputText}</span>;
    }
    const sortedHighlights = [...filteredHighlights].sort((a, b) => b.length - a.length);
    let remainingText = normalizedInputText;
    let result: React.ReactNode[] = [];
    let currentIndex = 0;

    sortedHighlights.forEach(highlight => {
      const cleanHighlight = highlight.replace(/[^\p{L}\p{N}\s'\u2019]/gu, '').trim();
      if (!cleanHighlight) return;

      const regex = new RegExp(escapeRegex(cleanHighlight), 'gi');
      const parts: string[] = [];
      let lastIndex = 0;
      let matchArr = regex.exec(remainingText);

      while (matchArr) {
        const matchStart = matchArr.index;
        const matchEnd = matchStart + matchArr[0].length;
        if (matchStart > lastIndex) {
          parts.push(remainingText.slice(lastIndex, matchStart));
        }
        parts.push(matchArr[0]);
        lastIndex = matchEnd;
        matchArr = regex.exec(remainingText);
      }

      if (lastIndex < remainingText.length) {
        parts.push(remainingText.slice(lastIndex));
      }

      const tempResult: React.ReactNode[] = parts.map((part, partIndex) => {
        const isMatch = part === cleanHighlight;
        const partKey = `nonspace-${hashString(part)}-${currentIndex}-${partIndex}`;
        return isMatch ? (
          <span key={partKey} className={styles.highlight} dir={isRTL(part) ? 'rtl' : 'ltr'}>
            {part}
          </span>
        ) : (
          <span key={partKey} dir={isRTL(part) ? 'rtl' : 'ltr'}>
            {part}
          </span>
        );
      });
      result = result.concat(tempResult);
      remainingText = parts.join('');
      currentIndex += 1;
    });
    return result.length > 0 ? (
      result
    ) : (
      <span dir={isRTL(normalizedInputText) ? 'rtl' : 'ltr'}>{normalizedInputText}</span>
    );
  }

  // Fallback: return original text if no matches found
  return <span dir={isRTL(normalizedInputText) ? 'rtl' : 'ltr'}>{normalizedInputText}</span>;
};

interface Props {
  darkMode: boolean;
  thumbnailUrl?: string;
  id: string;
  text: string;
  isSelected: boolean;
  reference: string;
  highlightedText: string[];
  select: () => void;
}

const SearchResult = ({
  darkMode,
  id,
  thumbnailUrl,
  text,
  reference,
  isSelected,
  highlightedText,
  select,
}: Props) => {
  const textStyle = useTextStyle({ large: '1.6em', normal: '1.2em', small: '1em' }, !isSelected);

  return (
    <Item id={id} darkMode={darkMode} select={select} bordered isSelected={isSelected}>
      {thumbnailUrl !== undefined && (
        <div className={styles.thumbnailContainer}>
          <img
            src={adjustHeight(thumbnailUrl, 80)}
            crossOrigin="anonymous"
            className={styles.thumbnail}
            alt=""
          />
        </div>
      )}
      <div style={textStyle} className={styles.textContainer}>
        <div className={styles.text}>{highlightText(text, highlightedText)}</div>
        <div className={styles.reference}>{reference}</div>
      </div>
    </Item>
  );
};

export default SearchResult;
